// v1.0.0 - 2026-01-31 - File uploader component with drag and drop

import { useState, useCallback, useRef } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface FileUploaderProps {
  onFileSelect: (file: File) => void
  accept?: string
  maxSize?: number // in bytes
  disabled?: boolean
}

export function FileUploader({
  onFileSelect,
  accept = '.xlsx,.xls',
  maxSize = 50 * 1024 * 1024, // 50MB
  disabled,
}: FileUploaderProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): string | null => {
    const allowedExtensions = accept.split(',').map((ext) => ext.trim().toLowerCase())
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()

    if (!allowedExtensions.some((ext) => ext === fileExtension || ext === '*')) {
      return `Tipo file non supportato. Usa: ${accept}`
    }

    if (file.size > maxSize) {
      return `File troppo grande. Max: ${Math.round(maxSize / 1024 / 1024)}MB`
    }

    return null
  }

  const handleFile = useCallback(
    (file: File) => {
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        return
      }

      setError(null)
      onFileSelect(file)
    },
    [onFileSelect, accept, maxSize]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (disabled) return

      const files = e.dataTransfer.files
      if (files.length > 0) {
        handleFile(files[0])
      }
    },
    [handleFile, disabled]
  )

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFile(files[0])
    }
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  return (
    <div className="space-y-2">
      <Card
        className={`cursor-pointer transition-colors ${
          isDragging ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/5' : 'border-dashed hover:border-[var(--color-accent)]/50'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <svg
            className="w-12 h-12 text-[var(--color-text-tertiary)] mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-lg font-medium mb-1 text-[var(--color-text-primary)]">
            {isDragging ? 'Rilascia il file qui' : 'Trascina un file qui'}
          </p>
          <p className="text-sm text-[var(--color-text-secondary)] mb-4">oppure clicca per selezionare</p>
          <Button variant="outline" type="button" disabled={disabled}>
            Scegli File
          </Button>
          <p className="text-xs text-[var(--color-text-tertiary)] mt-4">
            Formati supportati: Excel (.xlsx, .xls)
          </p>
        </CardContent>
      </Card>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {error && (
        <div className="rounded-md bg-[var(--color-bg-error)] p-3 text-sm text-[var(--color-error)]">{error}</div>
      )}
    </div>
  )
}
